import React, { useState, useCallback, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import {
  controlCenterApi,
  SalesforceAccount,
  SalesforceContact,
  AccountListItem,
} from '../../services/controlCenterApi'

// --- Types ---

interface SelectedContact {
  contact: SalesforceContact
  roleNames: string[]
}

interface AccountFormData {
  name: string
  email: string
  phone: string
  domain: string
  account_type: 'group' | 'rooftop'
  parent_id: string
}

const AVAILABLE_ROLES = ['admin', 'finance', 'gsuite']

const STEP_LABELS = [
  'SalesForce Search',
  'Select Contacts',
  'Account Details',
  'Review & Create',
]

// --- Main Wizard ---

export function CreateAccountWizard(): React.ReactElement {
  const navigate = useNavigate()
  const [currentStep, setCurrentStep] = useState(0)

  // Step 1 state
  const [sfAccount, setSfAccount] = useState<SalesforceAccount | null>(null)

  // Step 2 state
  const [selectedContacts, setSelectedContacts] = useState<SelectedContact[]>([])

  // Step 3 state
  const [accountForm, setAccountForm] = useState<AccountFormData>({
    name: '',
    email: '',
    phone: '',
    domain: '',
    account_type: 'rooftop',
    parent_id: '',
  })

  // Step 4 state
  const [creating, setCreating] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)

  const handleSfAccountSelected = useCallback((account: SalesforceAccount) => {
    setSfAccount(account)
    // Pre-fill account form from SF data
    setAccountForm((prev) => ({
      ...prev,
      name: account.name || '',
      phone: account.phone || '',
      domain: account.website || '',
    }))
    setCurrentStep(1)
  }, [])

  const handleContactsConfirmed = useCallback((contacts: SelectedContact[]) => {
    setSelectedContacts(contacts)
    // Pre-fill email from first contact if available
    if (contacts.length > 0 && contacts[0].contact.email) {
      setAccountForm((prev) => ({
        ...prev,
        email: prev.email || contacts[0].contact.email || '',
      }))
    }
    setCurrentStep(2)
  }, [])

  const handleAccountFormConfirmed = useCallback((form: AccountFormData) => {
    setAccountForm(form)
    setCurrentStep(3)
  }, [])

  const handleCreate = useCallback(async () => {
    if (!sfAccount) return
    setCreating(true)
    setCreateError(null)

    try {
      const result = await controlCenterApi.createAccount({
        account: {
          name: accountForm.name,
          email: accountForm.email,
          phone: accountForm.phone,
          domain: accountForm.domain || undefined,
          account_type: accountForm.account_type,
          parent_id: accountForm.parent_id || undefined,
        },
        users: selectedContacts.map((sc) => ({
          email: sc.contact.email || '',
          first_name: sc.contact.first_name,
          last_name: sc.contact.last_name,
          role_names: sc.roleNames,
        })),
        salesforce_account_id: sfAccount.sf_id,
      })

      navigate(`/control_center/accounts/${result.account.id}`)
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Failed to create account')
    } finally {
      setCreating(false)
    }
  }, [sfAccount, accountForm, selectedContacts, navigate])

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-1">
          <Link
            to="/control_center/accounts"
            className="text-slate-400 hover:text-white transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
            </svg>
          </Link>
          <h1 className="text-2xl font-bold text-white tracking-tight">Create Account</h1>
        </div>
        <p className="text-slate-400 text-sm ml-8">Search SalesForce, select contacts, and create a new dealer account.</p>
      </div>

      {/* Step Indicator */}
      <StepIndicator currentStep={currentStep} />

      {/* Step Content */}
      <div className="mt-8">
        {currentStep === 0 && (
          <SalesforceSearchStep onSelect={handleSfAccountSelected} />
        )}
        {currentStep === 1 && sfAccount && (
          <SelectContactsStep
            sfAccount={sfAccount}
            initialSelected={selectedContacts}
            onBack={() => setCurrentStep(0)}
            onConfirm={handleContactsConfirmed}
          />
        )}
        {currentStep === 2 && (
          <AccountDetailsStep
            initialData={accountForm}
            onBack={() => setCurrentStep(1)}
            onConfirm={handleAccountFormConfirmed}
          />
        )}
        {currentStep === 3 && sfAccount && (
          <ReviewStep
            sfAccount={sfAccount}
            selectedContacts={selectedContacts}
            accountForm={accountForm}
            creating={creating}
            error={createError}
            onBack={() => setCurrentStep(2)}
            onCreate={handleCreate}
          />
        )}
      </div>
    </div>
  )
}

// --- Step Indicator ---

function StepIndicator({ currentStep }: { currentStep: number }): React.ReactElement {
  return (
    <div className="flex items-center gap-0">
      {STEP_LABELS.map((label, i) => {
        const isActive = i === currentStep
        const isComplete = i < currentStep
        return (
          <React.Fragment key={label}>
            {i > 0 && (
              <div
                className={`flex-1 h-px mx-1 transition-colors duration-300 ${
                  isComplete ? 'bg-primary' : 'bg-slate-700'
                }`}
              />
            )}
            <div className="flex items-center gap-2 shrink-0">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all duration-300 ${
                  isActive
                    ? 'border-primary bg-primary/20 text-primary'
                    : isComplete
                      ? 'border-primary bg-primary text-slate-900'
                      : 'border-slate-600 bg-slate-800 text-slate-500'
                }`}
              >
                {isComplete ? (
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                  </svg>
                ) : (
                  i + 1
                )}
              </div>
              <span
                className={`text-xs font-medium hidden sm:block transition-colors duration-300 ${
                  isActive ? 'text-white' : isComplete ? 'text-primary' : 'text-slate-500'
                }`}
              >
                {label}
              </span>
            </div>
          </React.Fragment>
        )
      })}
    </div>
  )
}

// --- Step 1: SalesForce Search ---

function SalesforceSearchStep({
  onSelect,
}: {
  onSelect: (account: SalesforceAccount) => void
}): React.ReactElement {
  const [searchType, setSearchType] = useState<'name' | 'number'>('name')
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<SalesforceAccount[]>([])
  const [searching, setSearching] = useState(false)
  const [searched, setSearched] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSearch = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      if (!query.trim()) return
      setSearching(true)
      setError(null)
      setSearched(true)

      try {
        const params = searchType === 'name' ? { q: query.trim() } : { number: query.trim() }
        const accounts = await controlCenterApi.searchSalesforceAccounts(params)
        setResults(accounts)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'SalesForce search failed')
        setResults([])
      } finally {
        setSearching(false)
      }
    },
    [query, searchType]
  )

  return (
    <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
      <h2 className="text-lg font-semibold text-white mb-1">Search SalesForce</h2>
      <p className="text-sm text-slate-400 mb-5">Find the dealer account in SalesForce to import.</p>

      <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3 mb-6">
        <select
          value={searchType}
          onChange={(e) => setSearchType(e.target.value as 'name' | 'number')}
          className="bg-slate-900 border border-slate-600 text-white rounded-lg px-3 py-2.5 text-sm focus:border-primary focus:ring-1 focus:ring-primary outline-none sm:w-44"
        >
          <option value="name">Account Name</option>
          <option value="number">Account Number</option>
        </select>
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={searchType === 'name' ? 'Search by dealer name...' : 'Enter account number...'}
          className="flex-1 bg-slate-900 border border-slate-600 text-white rounded-lg px-4 py-2.5 text-sm placeholder-slate-500 focus:border-primary focus:ring-1 focus:ring-primary outline-none"
          autoFocus
        />
        <button
          type="submit"
          disabled={searching || !query.trim()}
          className="bg-primary hover:bg-primary/90 disabled:bg-slate-700 disabled:text-slate-500 text-slate-900 font-semibold rounded-lg px-5 py-2.5 text-sm transition-colors"
        >
          {searching ? (
            <span className="flex items-center gap-2">
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Searching
            </span>
          ) : (
            'Search'
          )}
        </button>
      </form>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-4">
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-red-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
            </svg>
            <p className="text-sm text-red-300">{error}</p>
          </div>
        </div>
      )}

      {searched && !searching && !error && results.length === 0 && (
        <div className="text-center py-10 text-slate-500">
          <svg className="w-10 h-10 mx-auto mb-3 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
          </svg>
          <p className="text-sm">No accounts found matching your search.</p>
        </div>
      )}

      {results.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-700">
                <th className="text-left py-3 px-3 text-slate-400 font-medium">Name</th>
                <th className="text-left py-3 px-3 text-slate-400 font-medium">Account #</th>
                <th className="text-left py-3 px-3 text-slate-400 font-medium hidden md:table-cell">Phone</th>
                <th className="text-left py-3 px-3 text-slate-400 font-medium hidden lg:table-cell">Location</th>
                <th className="py-3 px-3" />
              </tr>
            </thead>
            <tbody>
              {results.map((account) => (
                <tr
                  key={account.sf_id}
                  className="border-b border-slate-700/50 hover:bg-slate-700/30 transition-colors"
                >
                  <td className="py-3 px-3">
                    <div className="text-white font-medium">{account.name}</div>
                    {account.website && (
                      <div className="text-slate-500 text-xs mt-0.5">{account.website}</div>
                    )}
                  </td>
                  <td className="py-3 px-3 text-slate-300 font-mono text-xs">
                    {account.account_number || '—'}
                  </td>
                  <td className="py-3 px-3 text-slate-400 hidden md:table-cell">
                    {account.phone || '—'}
                  </td>
                  <td className="py-3 px-3 text-slate-400 hidden lg:table-cell">
                    {[account.city, account.state].filter(Boolean).join(', ') || '—'}
                  </td>
                  <td className="py-3 px-3 text-right">
                    <button
                      onClick={() => onSelect(account)}
                      className="text-primary hover:text-primary/80 font-medium text-sm transition-colors"
                    >
                      Select
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// --- Step 2: Select Contacts ---

function SelectContactsStep({
  sfAccount,
  initialSelected,
  onBack,
  onConfirm,
}: {
  sfAccount: SalesforceAccount
  initialSelected: SelectedContact[]
  onBack: () => void
  onConfirm: (contacts: SelectedContact[]) => void
}): React.ReactElement {
  const [contacts, setContacts] = useState<SalesforceContact[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selected, setSelected] = useState<Map<string, SelectedContact>>(
    () => new Map(initialSelected.map((sc) => [sc.contact.sf_id, sc]))
  )

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)

    controlCenterApi
      .getSalesforceContacts(sfAccount.sf_id)
      .then((data) => {
        if (!cancelled) setContacts(data)
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load contacts')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [sfAccount.sf_id])

  const toggleContact = useCallback((contact: SalesforceContact) => {
    setSelected((prev) => {
      const next = new Map(prev)
      if (next.has(contact.sf_id)) {
        next.delete(contact.sf_id)
      } else {
        next.set(contact.sf_id, { contact, roleNames: ['admin'] })
      }
      return next
    })
  }, [])

  const toggleRole = useCallback((sfId: string, role: string) => {
    setSelected((prev) => {
      const next = new Map(prev)
      const entry = next.get(sfId)
      if (entry) {
        const hasRole = entry.roleNames.includes(role)
        const newRoles = hasRole
          ? entry.roleNames.filter((r) => r !== role)
          : [...entry.roleNames, role]
        next.set(sfId, { ...entry, roleNames: newRoles })
      }
      return next
    })
  }, [])

  return (
    <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-lg font-semibold text-white">Select Contacts</h2>
          <p className="text-sm text-slate-400 mt-0.5">
            Choose contacts from <span className="text-primary font-medium">{sfAccount.name}</span> to create as VMS users.
          </p>
        </div>
        <span className="text-xs text-slate-500 bg-slate-900 rounded-md px-2.5 py-1">
          {selected.size} selected
        </span>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-12 text-slate-400">
          <svg className="w-5 h-5 animate-spin mr-2" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          Loading contacts...
        </div>
      )}

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-4">
          <p className="text-sm text-red-300">{error}</p>
        </div>
      )}

      {!loading && !error && contacts.length === 0 && (
        <div className="text-center py-10 text-slate-500">
          <p className="text-sm">No contacts found for this account. You can still proceed and add users manually later.</p>
        </div>
      )}

      {!loading && contacts.length > 0 && (
        <div className="space-y-2 mb-6">
          {contacts.map((contact) => {
            const isSelected = selected.has(contact.sf_id)
            const entry = selected.get(contact.sf_id)
            return (
              <div
                key={contact.sf_id}
                className={`border rounded-lg p-4 transition-all ${
                  isSelected
                    ? 'border-primary/40 bg-primary/5'
                    : 'border-slate-700/50 bg-slate-900/30 hover:border-slate-600'
                }`}
              >
                <div className="flex items-start gap-3">
                  <label className="mt-0.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleContact(contact)}
                      className="w-4 h-4 rounded border-slate-600 text-primary focus:ring-primary bg-slate-800"
                    />
                  </label>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-white font-medium text-sm">
                        {contact.first_name} {contact.last_name}
                      </span>
                      {contact.title && (
                        <span className="text-xs text-slate-500">{contact.title}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-4 mt-1 text-xs text-slate-400">
                      {contact.email && <span>{contact.email}</span>}
                      {contact.phone && <span>{contact.phone}</span>}
                      {!contact.email && (
                        <span className="text-amber-400">No email — cannot create user</span>
                      )}
                    </div>

                    {isSelected && contact.email && (
                      <div className="mt-3 flex items-center gap-2 flex-wrap">
                        <span className="text-xs text-slate-500 mr-1">Roles:</span>
                        {AVAILABLE_ROLES.map((role) => (
                          <button
                            key={role}
                            onClick={() => toggleRole(contact.sf_id, role)}
                            className={`text-xs px-2.5 py-1 rounded-md border transition-colors ${
                              entry?.roleNames.includes(role)
                                ? 'border-primary/50 bg-primary/15 text-primary'
                                : 'border-slate-700 bg-slate-800 text-slate-500 hover:text-slate-300'
                            }`}
                          >
                            {role}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      <WizardNav
        onBack={onBack}
        onNext={() => onConfirm(Array.from(selected.values()).filter((sc) => sc.contact.email))}
        nextLabel={selected.size > 0 ? `Continue with ${selected.size} user${selected.size > 1 ? 's' : ''}` : 'Skip — No Users'}
        disabled={loading}
      />
    </div>
  )
}

// --- Step 3: Account Details ---

function AccountDetailsStep({
  initialData,
  onBack,
  onConfirm,
}: {
  initialData: AccountFormData
  onBack: () => void
  onConfirm: (form: AccountFormData) => void
}): React.ReactElement {
  const [form, setForm] = useState<AccountFormData>(initialData)
  const [groups, setGroups] = useState<AccountListItem[]>([])
  const [errors, setErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    controlCenterApi.getGroupAccounts().then(setGroups).catch(() => {})
  }, [])

  const updateField = useCallback((field: keyof AccountFormData, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }))
    setErrors((prev) => {
      const next = { ...prev }
      delete next[field]
      return next
    })
  }, [])

  const validate = useCallback((): boolean => {
    const errs: Record<string, string> = {}
    if (!form.name.trim()) errs.name = 'Account name is required'
    if (!form.email.trim()) errs.email = 'Email is required'
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = 'Invalid email format'
    if (!form.phone.trim()) errs.phone = 'Phone is required'
    setErrors(errs)
    return Object.keys(errs).length === 0
  }, [form])

  const handleSubmit = useCallback(() => {
    if (validate()) onConfirm(form)
  }, [form, validate, onConfirm])

  return (
    <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
      <h2 className="text-lg font-semibold text-white mb-1">Account Details</h2>
      <p className="text-sm text-slate-400 mb-6">Review and adjust the account information before creating.</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
        <FormField
          label="Account Name"
          value={form.name}
          onChange={(v) => updateField('name', v)}
          error={errors.name}
          required
        />
        <FormField
          label="Email"
          type="email"
          value={form.email}
          onChange={(v) => updateField('email', v)}
          error={errors.email}
          required
        />
        <FormField
          label="Phone"
          value={form.phone}
          onChange={(v) => updateField('phone', v)}
          error={errors.phone}
          required
        />
        <FormField
          label="Domain"
          value={form.domain}
          onChange={(v) => updateField('domain', v)}
          placeholder="e.g. dealership.com"
        />

        <div>
          <label className="block text-sm text-slate-300 mb-1.5 font-medium">Account Type</label>
          <select
            value={form.account_type}
            onChange={(e) => updateField('account_type', e.target.value)}
            className="w-full bg-slate-900 border border-slate-600 text-white rounded-lg px-3 py-2.5 text-sm focus:border-primary focus:ring-1 focus:ring-primary outline-none"
          >
            <option value="rooftop">Rooftop</option>
            <option value="group">Group</option>
          </select>
        </div>

        {form.account_type === 'rooftop' && groups.length > 0 && (
          <div>
            <label className="block text-sm text-slate-300 mb-1.5 font-medium">Parent Group</label>
            <select
              value={form.parent_id}
              onChange={(e) => updateField('parent_id', e.target.value)}
              className="w-full bg-slate-900 border border-slate-600 text-white rounded-lg px-3 py-2.5 text-sm focus:border-primary focus:ring-1 focus:ring-primary outline-none"
            >
              <option value="">None (standalone)</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      <WizardNav onBack={onBack} onNext={handleSubmit} nextLabel="Review" />
    </div>
  )
}

// --- Step 4: Review & Create ---

function ReviewStep({
  sfAccount,
  selectedContacts,
  accountForm,
  creating,
  error,
  onBack,
  onCreate,
}: {
  sfAccount: SalesforceAccount
  selectedContacts: SelectedContact[]
  accountForm: AccountFormData
  creating: boolean
  error: string | null
  onBack: () => void
  onCreate: () => void
}): React.ReactElement {
  return (
    <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
      <h2 className="text-lg font-semibold text-white mb-1">Review & Create</h2>
      <p className="text-sm text-slate-400 mb-6">Verify everything looks correct before creating the account.</p>

      {/* SF Account */}
      <ReviewSection title="SalesForce Account">
        <ReviewRow label="Name" value={sfAccount.name} />
        <ReviewRow label="Account #" value={sfAccount.account_number || '—'} />
        <ReviewRow label="SF ID" value={sfAccount.sf_id} mono />
      </ReviewSection>

      {/* Account Details */}
      <ReviewSection title="VMS Account">
        <ReviewRow label="Name" value={accountForm.name} />
        <ReviewRow label="Email" value={accountForm.email} />
        <ReviewRow label="Phone" value={accountForm.phone} />
        <ReviewRow label="Domain" value={accountForm.domain || '—'} />
        <ReviewRow label="Type" value={accountForm.account_type} />
      </ReviewSection>

      {/* Users */}
      <ReviewSection title={`Users (${selectedContacts.length})`}>
        {selectedContacts.length === 0 ? (
          <p className="text-sm text-slate-500 py-2">No users will be created. You can add users later.</p>
        ) : (
          <div className="space-y-2">
            {selectedContacts.map((sc) => (
              <div
                key={sc.contact.sf_id}
                className="flex items-center justify-between py-2 border-b border-slate-700/30 last:border-0"
              >
                <div>
                  <span className="text-sm text-white">
                    {sc.contact.first_name} {sc.contact.last_name}
                  </span>
                  <span className="text-xs text-slate-500 ml-2">{sc.contact.email}</span>
                </div>
                <div className="flex items-center gap-1">
                  {sc.roleNames.map((role) => (
                    <span
                      key={role}
                      className="text-xs px-2 py-0.5 rounded bg-primary/15 text-primary border border-primary/30"
                    >
                      {role}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </ReviewSection>

      {/* Actions */}
      <div className="mt-2 p-4 bg-slate-900/50 rounded-lg border border-slate-700/30">
        <p className="text-xs text-slate-400 mb-1">After creation:</p>
        <ul className="text-xs text-slate-500 list-disc list-inside space-y-0.5">
          <li>A SalesForce case will be created for the new account</li>
          {selectedContacts.length > 0 && <li>Welcome emails will be queued for new users</li>}
          <li>You will be redirected to the account detail page</li>
        </ul>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mt-4">
          <p className="text-sm text-red-300">{error}</p>
        </div>
      )}

      <div className="flex items-center justify-between mt-6 pt-5 border-t border-slate-700/50">
        <button
          onClick={onBack}
          disabled={creating}
          className="text-sm text-slate-400 hover:text-white transition-colors disabled:opacity-50"
        >
          Back
        </button>
        <button
          onClick={onCreate}
          disabled={creating}
          className="bg-primary hover:bg-primary/90 disabled:bg-primary/50 text-slate-900 font-bold rounded-lg px-8 py-3 text-sm transition-colors flex items-center gap-2"
        >
          {creating ? (
            <>
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Creating...
            </>
          ) : (
            'Create Account'
          )}
        </button>
      </div>
    </div>
  )
}

// --- Shared Components ---

function WizardNav({
  onBack,
  onNext,
  nextLabel = 'Continue',
  disabled = false,
}: {
  onBack?: () => void
  onNext: () => void
  nextLabel?: string
  disabled?: boolean
}): React.ReactElement {
  return (
    <div className="flex items-center justify-between pt-5 border-t border-slate-700/50">
      {onBack ? (
        <button
          onClick={onBack}
          className="text-sm text-slate-400 hover:text-white transition-colors"
        >
          Back
        </button>
      ) : (
        <div />
      )}
      <button
        onClick={onNext}
        disabled={disabled}
        className="bg-primary hover:bg-primary/90 disabled:bg-slate-700 disabled:text-slate-500 text-slate-900 font-semibold rounded-lg px-6 py-2.5 text-sm transition-colors"
      >
        {nextLabel}
      </button>
    </div>
  )
}

let fieldIdCounter = 0

function FormField({
  label,
  value,
  onChange,
  error,
  required,
  type = 'text',
  placeholder,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  error?: string
  required?: boolean
  type?: string
  placeholder?: string
}): React.ReactElement {
  const [id] = useState(() => `field-${++fieldIdCounter}`)
  return (
    <div>
      <label htmlFor={id} className="block text-sm text-slate-300 mb-1.5 font-medium">
        {label}
        {required && <span className="text-primary ml-0.5">*</span>}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`w-full bg-slate-900 border text-white rounded-lg px-3 py-2.5 text-sm placeholder-slate-500 outline-none transition-colors ${
          error
            ? 'border-red-500 focus:border-red-400 focus:ring-1 focus:ring-red-400'
            : 'border-slate-600 focus:border-primary focus:ring-1 focus:ring-primary'
        }`}
      />
      {error && <p className="text-xs text-red-400 mt-1">{error}</p>}
    </div>
  )
}

function ReviewSection({
  title,
  children,
}: {
  title: string
  children: React.ReactNode
}): React.ReactElement {
  return (
    <div className="mb-5">
      <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">{title}</h3>
      <div className="bg-slate-900/40 rounded-lg border border-slate-700/30 p-4">{children}</div>
    </div>
  )
}

function ReviewRow({
  label,
  value,
  mono,
}: {
  label: string
  value: string
  mono?: boolean
}): React.ReactElement {
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-slate-700/20 last:border-0">
      <span className="text-xs text-slate-500">{label}</span>
      <span className={`text-sm text-white ${mono ? 'font-mono text-xs' : ''}`}>{value}</span>
    </div>
  )
}
